import { useState, useRef, useEffect } from 'react'
import Player from '@vimeo/player'

function VideoPlayer({ src, poster, vimeoId, vimeoUrl }) {
  const [isMuted, setIsMuted] = useState(true)
  const [showControls, setShowControls] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isVimeoReady, setIsVimeoReady] = useState(false)
  const videoRef = useRef(null)
  const vimeoContainerRef = useRef(null)
  const vimeoPlayerRef = useRef(null)

  const isVimeo = Boolean(vimeoId || vimeoUrl)

  useEffect(() => {
    if (isVimeo && vimeoContainerRef.current) {
      const options = {
        autoplay: true,
        muted: true,
        loop: true,
        playsinline: true,
        autopause: false,
        byline: false,
        portrait: false,
        title: false,
        dnt: true,
        controls: true
      }

      if (vimeoId) {
        options.id = Number(vimeoId)
      } else if (vimeoUrl) {
        options.url = vimeoUrl
      }

      const player = new Player(vimeoContainerRef.current, options)
      vimeoPlayerRef.current = player

      player.ready()
        .then(() => {
          setIsVimeoReady(true)
          return player.play().catch(() => {})
        })
        .catch(() => {
          setIsVimeoReady(false)
        })

      const handlePlay = () => setIsPlaying(true)
      const handlePause = () => setIsPlaying(false)

      player.on('play', handlePlay)
      player.on('pause', handlePause)

      return () => {
        player.off('play', handlePlay)
        player.off('pause', handlePause)
        player.destroy().catch(() => {})
        vimeoPlayerRef.current = null
        setIsVimeoReady(false)
      }
    }

    if (!isVimeo && videoRef.current) {
      videoRef.current.play().catch(err => {
        console.log('Autoplay failed:', err)
      })
    }
  }, [isVimeo, vimeoId, vimeoUrl])

  const handlePlayWithSound = () => {
    if (isVimeo && vimeoPlayerRef.current) {
      if (!isVimeoReady) return
      vimeoPlayerRef.current.setMuted(false).catch(() => {})
      vimeoPlayerRef.current.setVolume(1).catch(() => {})
      vimeoPlayerRef.current.setCurrentTime(0).catch(() => {})
      vimeoPlayerRef.current.play().catch(() => {})
      setIsMuted(false)
      setShowControls(true)
      setIsPlaying(true)
      return
    }

    if (videoRef.current) {
      videoRef.current.currentTime = 0
      videoRef.current.muted = false
      videoRef.current.volume = 1.0
      videoRef.current.play()
      setIsMuted(false)
      setShowControls(true)
      setIsPlaying(true)
    }
  }

  const handleVideoPlay = () => setIsPlaying(true)
  const handleVideoPause = () => setIsPlaying(false)

  const shouldShowOverlay = isMuted && (!isVimeo || (isVimeo && isVimeoReady))

  return (
    <div className="relative w-full h-full rounded-lg overflow-hidden bg-gray-900">
      {isVimeo ? (
        <div
          ref={vimeoContainerRef}
          className="absolute inset-0 [&>iframe]:w-full [&>iframe]:h-full [&>iframe]:absolute [&>iframe]:inset-0"
          aria-label="Vimeo video player"
        />
      ) : (
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          muted={isMuted}
          autoPlay
          loop
          playsInline
          preload="auto"
          controls={showControls}
          onPlay={handleVideoPlay}
          onPause={handleVideoPause}
          className="w-full h-full object-cover"
        >
          Your browser does not support the video tag.
        </video>
      )}
      
      {/* Sound control overlay - only show when muted */}
      {shouldShowOverlay && (
        <div 
          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 cursor-pointer transition-opacity hover:bg-opacity-40"
          onClick={handlePlayWithSound}
        >
          <div className="text-center">
            {/* Play button circle */}
            <div className="w-20 h-20 bg-white bg-opacity-90 rounded-full flex items-center justify-center mb-3 mx-auto shadow-lg hover:bg-opacity-100 transition-all hover:scale-105">
              <svg 
                className="w-10 h-10 text-primary-600 ml-1" 
                fill="currentColor" 
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
            <p className="text-white font-semibold text-lg drop-shadow-md">
              Click for sound
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default VideoPlayer
